'use client';

import { isSameEndpointQuery } from '@common/api/isSameEndpointQuery';
import { hasObjectKey } from '@common/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneDeep, debounce } from 'es-toolkit';
import { useMemo } from 'react';

import { AuthApi, AuthQueryKey } from '@/api/v2';
import { ROUTES } from '@/constants';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';

import { useCustomRouter } from './useCustomRouter';

interface BookmarkParams {
  /**
   * 쿼리키 조회용
   */
  idx?: string;
  contentId: number;
  isBookmarked: boolean;
  bookmarkType: string;
}

// 북마크 가능한 항목의 인터페이스
interface BookmarkableItem {
  id: number;
  wishlist_yn: boolean;
  [key: string]: unknown;
}

// 컨텐츠 배열을 포함하는 응답 데이터 인터페이스
interface ContentsResponse {
  context: {
    contents: BookmarkableItem[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const OPTIMISTIC_QUERY_KEYS = [
  AuthQueryKey['get:/wello/v2/auth/member/neighbor/policy'],
  AuthQueryKey['get:/wello/v2/auth/member/recommend/policy'],
  AuthQueryKey['get:/wello/v2/auth/policy-apply/history'],
  AuthQueryKey['get:/wello/v2/auth/community/wello-news'],
  AuthQueryKey['get:/wello/v2/auth/member/recent/policy'],
  AuthQueryKey['get:/wello/v2/auth/community/policy-talk'],
  AuthQueryKey['get:/wello/v2/auth/hometown-news'],
  AuthQueryKey['get:/wello/v2/auth/member/interest/policy'],
  AuthQueryKey['get:/wello/v2/auth/policy/policy/{meta_policy_id_idx}/related'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/voucher-provider'],
  AuthQueryKey['get:/wello/v2/auth/contents/pick/{wello_pick_id}'],
  AuthQueryKey['get:/wello/v2/auth/policy'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/policy'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/community/policy-talk'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/best-contents'],
  AuthQueryKey['get:/wello/v2/auth/wello-comment'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/emergency-room'],
  AuthQueryKey['get:/wello/v2/auth/emergency-room/{emergencyRoomId}'],
  AuthQueryKey['get:/wello/v2/auth/voucher/provider'],
  AuthQueryKey['get:/wello/v2/auth/voucher/provider/feedback/{id}'],
  AuthQueryKey['get:/wello/v2/auth/policy/feedback/{policy_id}'],
  AuthQueryKey['get:/wello/v2/auth/contents/feedback/{best_contents_id}'],
  AuthQueryKey['get:/wello/v2/auth/wello-comment/my'],
  AuthQueryKey['get:/wello/v2/auth/community/my'],
  AuthQueryKey['get:/wello/v2/auth/hometown-news/feedback/{hometown_news_id}'],
  AuthQueryKey[
    'get:/wello/v2/auth/community/free-talk/feedback/{community_id_idx}'
  ],
  AuthQueryKey[
    'get:/wello/v2/auth/community/policy-talk/feedback/{community_id_idx}'
  ],
  AuthQueryKey[
    'get:/wello/v2/auth/community/wello-news/feedback/{community_id_idx}'
  ],
  AuthQueryKey['get:/wello/v2/auth/member/peer-group/policy'],
  AuthQueryKey['get:/wello/v2/auth/contents/best'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/hometown-news'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/community/wello-news'],
];

type QueryObject = {
  query: {
    page: number;
  };
};

const isQueryObject = (obj: object): obj is QueryObject => {
  return 'query' in obj && typeof (obj as any).query === 'object';
};

export const useBookmark = () => {
  const { mutate } = useMutation({
    mutationFn: ({ contentId, isBookmarked, bookmarkType }: BookmarkParams) =>
      AuthApi[
        isBookmarked
          ? 'delete:/wello/v2/auth/member/wishlist'
          : 'post:/wello/v2/auth/member/wishlist'
      ]({
        body: {
          content_id: contentId,
          content_type_cd: bookmarkType,
        },
      }),
    meta: {
      reset: [AuthQueryKey['get:/wello/v2/auth/member/action-count']()],
    },
  });

  const queryClient = useQueryClient();
  const router = useCustomRouter();

  const { request: requestHaptic } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC,
  });

  const bookmark = useMemo(() => {
    const debouncedMutate = debounce<typeof mutate>((params, options) => {
      return mutate(params, options);
    }, 500);

    return (params: BookmarkParams) => {
      requestHaptic();
      const { contentId, isBookmarked, idx } = params;

      const { isLogin } = useAuthStore.getState();

      if (!isLogin)
        return router.push(
          ROUTES.LOGIN.withSearchParams({
            searchParams: {
              redirect: location.href,
            },
          }),
        );

      const bookmarkQueries = queryClient.getQueriesData({
        predicate: (query) =>
          OPTIMISTIC_QUERY_KEYS.some((queryKey) =>
            isSameEndpointQuery(query, queryKey()),
          ),
      });

      bookmarkQueries.forEach(([key, data]) => {
        const isBaseKeyHasId = (() => {
          const [firstKey] = key;

          const isV2ApiQueryKey = typeof firstKey === 'object';

          if (isV2ApiQueryKey) {
            const StringKey = JSON.stringify(firstKey);

            const id = String(contentId);

            if (StringKey.includes(id) || (idx && StringKey.includes(idx)))
              return true;
          }

          return false;
        })();

        if (
          (key.includes(contentId) ||
            isBaseKeyHasId ||
            (idx && key.includes(idx))) &&
          hasObjectKey(data, 'context') &&
          hasObjectKey(data.context, 'wishlist_yn')
        ) {
          const newData = cloneDeep(data);

          if (
            hasObjectKey(newData.context, 'wishlist_yn') &&
            typeof newData.context.wishlist_yn === 'boolean'
          ) {
            newData.context.wishlist_yn = !isBookmarked;

            if (
              'wishlist_count' in newData.context &&
              typeof newData.context.wishlist_count === 'number'
            ) {
              newData.context.wishlist_count = isBookmarked
                ? newData.context.wishlist_count - 1
                : newData.context.wishlist_count + 1;
            }

            queryClient.setQueryData(key, newData);
          }
        }
        //* 일반 쿼리 (not Infinite Query)
        else if (
          data &&
          typeof data === 'object' &&
          hasObjectKey(data, 'context') &&
          data.context &&
          typeof data.context === 'object' &&
          hasObjectKey(data.context, 'contents') &&
          Array.isArray(data.context.contents)
        ) {
          const newData = cloneDeep(data);

          const updateContents = (contents: unknown[]): unknown[] =>
            contents.map((item) => {
              if (
                item &&
                typeof item === 'object' &&
                'id' in item &&
                typeof item.id === 'number' &&
                item.id === contentId &&
                'wishlist_yn' in item &&
                typeof item.wishlist_yn === 'boolean'
              ) {
                return { ...item, wishlist_yn: !isBookmarked };
              }

              return item;
            });

          const hasContents = (obj: unknown): obj is ContentsResponse =>
            obj !== null &&
            typeof obj === 'object' &&
            'context' in obj &&
            obj.context !== null &&
            typeof obj.context === 'object' &&
            'contents' in obj.context &&
            Array.isArray(obj.context.contents);

          if (hasContents(newData)) {
            const updatedContents = updateContents(newData.context.contents);
            const hasChanges = updatedContents.some(
              (item, index) => item !== newData.context.contents[index],
            );

            if (hasChanges) {
              newData.context.contents = updatedContents as BookmarkableItem[];
              queryClient.setQueryData(key, newData);
            }
          }
        } else
          for (const idKey of [
            'community_id',
            'best_contents_id',
            'hometown_news_id',
            'wello_comment_id',
            'policy_talk_id',
            'policy_id',
            'id',
          ] as const) {
            const newData = cloneDeep(data);

            const targetObjectList = ((data) => {
              const result: unknown[] = [];

              const findTargetObject = (obj: unknown) => {
                if (obj && typeof obj === 'object') {
                  if (hasObjectKey(obj, idKey) && obj[idKey] === contentId) {
                    switch (idKey) {
                      case 'id': {
                        const hasId =
                          'meta_policy_id_idx' in obj || 'uuid' in obj;

                        if (hasId) result.push(obj);

                        break;
                      }

                      default:
                        result.push(obj);
                        break;
                    }
                  }

                  for (const key in obj) {
                    if (hasObjectKey(obj, key)) {
                      findTargetObject(obj[key]);
                    }
                  }
                }
              };

              findTargetObject(data);

              return result;
            })(newData);

            const changeBookmarkCount = (obj: unknown) => {
              if (obj && typeof obj === 'object') {
                if (
                  hasObjectKey(obj, 'wishlist_count') &&
                  typeof obj.wishlist_count === 'number'
                ) {
                  obj['wishlist_count'] = isBookmarked
                    ? obj['wishlist_count'] - 1
                    : obj['wishlist_count'] + 1;
                }
                for (const key in obj) {
                  if (hasObjectKey(obj, key)) {
                    changeBookmarkCount(obj[key]);
                  }
                }
              }
            };

            const changeBookmarkYn = (obj: unknown) => {
              if (obj && typeof obj === 'object') {
                if (
                  hasObjectKey(obj, 'wishlist_yn') &&
                  typeof obj.wishlist_yn === 'boolean'
                ) {
                  obj['wishlist_yn'] = !isBookmarked;
                }
                for (const key in obj) {
                  if (hasObjectKey(obj, key)) {
                    changeBookmarkYn(obj[key]);
                  }
                }
              }
            };

            targetObjectList.forEach((obj) => {
              changeBookmarkCount(obj);
              changeBookmarkYn(obj);

              const [firstKey] = key;

              const isV2InfiniteQuery =
                firstKey && isQueryObject(firstKey) && '_infinite';

              if (isV2InfiniteQuery) {
                firstKey.query.page = 1;
              }

              queryClient.setQueryData(key, newData);
            });
          }
      });

      debouncedMutate(params, {
        onError: () => {
          bookmarkQueries.forEach(([key, data]) => {
            queryClient.setQueryData(key, data);
          });
        },
      });
    };
  }, [mutate, queryClient, requestHaptic, router]);

  return { bookmark };
};
